'use client'

import { useState, useEffect, useRef } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Lightbulb, Lock, RefreshCw, X, Send, User } from 'lucide-react'
import { useSubscription } from '@/hooks/use-subscription'
import { hasFeature } from '@/lib/features'
import { useRouter, usePathname } from 'next/navigation'

// ─── Types ────────────────────────────────────────────────────────────────────
interface CoachAdvice {
    title: string
    message: string
    tips: string[]
    mood: 'positive' | 'neutral' | 'warning'
}

interface ChatMessage {
    role: 'user' | 'model'
    content: string
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CACHE_KEY = 'pw_coach_advice'
const CACHE_TS_KEY = 'pw_coach_advice_ts'
const CACHE_TTL_HOURS = 6
type Tab = 'analysis' | 'chat'

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTimeSince(ts: number): string {
    const diffMin = Math.floor((Date.now() - ts) / 60000)
    if (diffMin < 1) return 'ahora'
    if (diffMin < 60) return `${diffMin}m`
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24) return `${diffH}h`
    return `${Math.floor(diffH / 24)}d`
}

function WiseLogo({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
    const sizeMap = { sm: 'text-xs', md: 'text-sm', lg: 'text-lg', xl: 'text-xl' }
    return <span className={`${sizeMap[size]} font-black italic leading-none select-none`}>W</span>
}

function MoodBadge({ mood }: { mood: CoachAdvice['mood'] }) {
    if (mood === 'positive') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-600 border border-green-500/20">🚀 Bien</span>
    if (mood === 'warning') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-500 border border-orange-500/20">👀 Ojo</span>
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-500 border border-blue-500/20">😌 Tranqui</span>
}

function getGreeting(): string {
    const hour = new Date().getHours()
    if (hour < 12) return '¡Buenos días! ☀️'
    if (hour < 18) return '¡Buenas tardes! 👋'
    return '¡Buenas noches! 🌙'
}

function getRandomSuggestions(): string[] {
    const pools = [
        ['¿Cómo voy este mes?', '¿Me puedo dar un gusto de $100k?', '¿En qué gasto más?'],
        ['¿Estoy gastando más que el mes pasado?', '¿Cuánto llevo ahorrado?', 'Dame un consejo rápido'],
        ['¿Cómo va mi balance?', '¿En qué debería recortar?', 'Resumen rápido de mis finanzas'],
    ]
    return pools[Math.floor(Math.random() * pools.length)]
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function AICoachFAB() {
    const { data: sub, loading: subLoading } = useSubscription()
    const router = useRouter()
    const pathname = usePathname()
    const isPro = hasFeature('scanner_ai', sub)

    const [isOpen, setIsOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<Tab>('analysis')

    // Analysis state
    const [advice, setAdvice] = useState<CoachAdvice | null>(null)
    const [analysisLoading, setAnalysisLoading] = useState(false)
    const [analysisError, setAnalysisError] = useState('')
    const [lastTs, setLastTs] = useState<number | null>(null)

    // Chat state
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
    const [chatInput, setChatInput] = useState('')
    const [chatLoading, setChatLoading] = useState(false)
    const chatEndRef = useRef<HTMLDivElement>(null)
    const [suggestions] = useState(getRandomSuggestions)

    // Load cache on mount
    useEffect(() => {
        if (!isPro) return
        try {
            const cached = localStorage.getItem(CACHE_KEY)
            const ts = localStorage.getItem(CACHE_TS_KEY)
            if (cached && ts) {
                const tsNum = parseInt(ts, 10)
                setLastTs(tsNum)
                setAdvice(JSON.parse(cached))
                if ((Date.now() - tsNum) / 3600000 >= CACHE_TTL_HOURS) {
                    fetchAnalysis(true)
                }
            }
        } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPro])

    // Auto-scroll chat
    useEffect(() => {
        if (isOpen && activeTab === 'chat') {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [chatHistory, chatLoading, isOpen, activeTab])

    // Fetch on first open if no cache
    useEffect(() => {
        if (isOpen && isPro && !advice && !analysisLoading && !analysisError) {
            fetchAnalysis()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isPro, advice])

    // ── Analysis fetch ─────────────────────────────────────────────────────────
    const fetchAnalysis = async (silent = false) => {
        if (!isPro) return
        if (!silent) { setAnalysisLoading(true); setAnalysisError('') }
        try {
            const res = await fetch('/api/ai-coach', { method: 'POST' })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Error al consultar al coach')
            const now = Date.now()
            setAdvice(data)
            setLastTs(now)
            try {
                localStorage.setItem(CACHE_KEY, JSON.stringify(data))
                localStorage.setItem(CACHE_TS_KEY, String(now))
            } catch { /* ignore */ }
        } catch (err: any) {
            if (!silent) setAnalysisError(err.message || 'Wise está recargando energías. Intenta en un momento.')
        } finally {
            if (!silent) setAnalysisLoading(false)
        }
    }

    // ── Chat send ──────────────────────────────────────────────────────────────
    const sendMessage = async () => {
        const msg = chatInput.trim()
        if (!msg || chatLoading) return

        const newHistory: ChatMessage[] = [...chatHistory, { role: 'user', content: msg }]
        setChatHistory(newHistory)
        setChatInput('')
        setChatLoading(true)

        try {
            const res = await fetch('/api/ai-coach/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: msg, history: chatHistory })
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Error al conectar con el coach')
            setChatHistory([...newHistory, { role: 'model', content: data.reply }])
        } catch (err: any) {
            setChatHistory([...newHistory, { role: 'model', content: '😵 Se me cruzaron los cables. ¿Intentas de nuevo?' }])
        } finally {
            setChatLoading(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
    }

    // ── Sub Loading state ──────────────────────────────────────────────────────
    if (subLoading) return null // Hide FAB while loading subscription

    // ── Analysis content ───────────────────────────────────────────────────────
    const moodBorderColor =
        advice?.mood === 'positive' ? 'text-green-600 bg-green-50 dark:bg-green-900/20 border-green-200' :
        advice?.mood === 'warning'  ? 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 border-orange-200' :
                                      'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200'

    const analysisContent = (() => {
        if (!isPro) {
            return (
                <div className="flex flex-col items-center justify-center py-10 px-6 text-center h-full">
                    <div className="bg-primary/10 p-4 rounded-full mb-4">
                        <Lock className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-semibold text-xl mb-2">Conoce a Wise</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-[250px]">
                        Tu coach financiero personal. Analiza tus datos, te da consejos sin rodeos y te ayuda a tomar mejores decisiones con tu dinero.
                    </p>
                    <Button onClick={() => { setIsOpen(false); router.push('/dashboard/plans') }} className="bg-orange-600 hover:bg-orange-700 text-white border-0 w-full">
                        Desbloquear con Pro
                    </Button>
                </div>
            )
        }
        if (analysisLoading) return (
            <div className="space-y-4 pt-4 px-4 pb-6">
                <div className="h-5 bg-muted rounded w-3/4 animate-pulse" />
                <div className="h-4 bg-muted rounded w-full animate-pulse" />
                <div className="h-24 bg-muted rounded w-full animate-pulse" />
            </div>
        )
        if (analysisError) return (
            <div className="flex flex-col items-center justify-center py-10 text-center text-red-500 text-sm px-4">
                <p className="mb-4 text-muted-foreground">{analysisError}</p>
                <Button variant="outline" size="sm" onClick={() => fetchAnalysis()}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Reintentar
                </Button>
            </div>
        )
        if (!advice) return (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-1 animate-pulse">
                    <WiseLogo size="lg" />
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                    Wise está analizando tus números...
                </p>
                <p className="text-xs text-muted-foreground/70">Un momento</p>
            </div>
        )
        return (
            <div className="space-y-5 px-4 pt-4 pb-6 overflow-y-auto">
                <div className={`p-4 rounded-xl border ${moodBorderColor}`}>
                    <h3 className="font-bold text-base mb-2">{advice.title}</h3>
                    <p className="text-sm opacity-90 leading-relaxed">{advice.message}</p>
                </div>
                <div className="space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-emerald-500" /> Lo que Wise te recomienda:
                    </h4>
                    <ul className="space-y-2">
                        {advice.tips.map((tip, i) => (
                            <li key={i} className="text-sm flex items-start gap-3 bg-muted/50 p-3 rounded-lg">
                                <span className="text-primary font-bold mt-0.5">•</span>
                                <span className="leading-relaxed">{tip}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        )
    })()

    // ── Chat content ───────────────────────────────────────────────────────────
    const chatContent = (
        <div className="flex flex-col h-full bg-background">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {!isPro && (
                    <div className="flex flex-col items-center justify-center h-full text-center p-4">
                        <Lock className="h-8 w-8 text-muted-foreground mb-3 opacity-50" />
                        <p className="text-sm text-muted-foreground">Chat inteligente disponible en el plan Pro.</p>
                    </div>
                )}
                {isPro && chatHistory.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-8 space-y-3 px-4">
                        <div className="h-14 w-14 mx-auto rounded-full bg-orange-600 flex items-center justify-center shadow-lg mb-4 text-white">
                            <WiseLogo size="xl" />
                        </div>
                        <p className="font-semibold text-foreground text-base">{getGreeting()}</p>
                        <p className="text-xs leading-relaxed max-w-[260px] mx-auto">
                            Soy <span className="font-medium text-primary">Wise</span>, tu coach financiero. Pregúntame lo que sea sobre tus finanzas — sin filtro, sin rodeos.
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center mt-5">
                            {suggestions.map(s => (
                                <button
                                    key={s}
                                    onClick={() => { setChatInput(s); }}
                                    className="text-xs px-3 py-2 rounded-2xl border border-primary/20 bg-primary/5 hover:bg-primary/15 hover:border-primary/40 transition-all text-foreground shadow-sm"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {isPro && chatHistory.map((msg, i) => (
                    <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'model' && (
                            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-600 flex items-center justify-center shadow-sm text-white">
                                <WiseLogo size="sm" />
                            </div>
                        )}
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                            msg.role === 'user'
                                ? 'bg-primary text-primary-foreground rounded-br-sm'
                                : 'bg-muted text-foreground rounded-bl-sm'
                        }`}>
                            {msg.content}
                        </div>
                        {msg.role === 'user' && (
                            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent flex items-center justify-center border border-border">
                                <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                        )}
                    </div>
                ))}
                {chatLoading && (
                    <div className="flex gap-3 justify-start">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-600 flex items-center justify-center shadow-sm text-white">
                            <WiseLogo size="sm" />
                        </div>
                        <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-1.5 h-[42px]">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:0ms]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:150ms]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:300ms]" />
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>
            
            {/* Input */}
            {isPro && (
                <div className="p-3 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                    <div className="flex gap-2 bg-muted/50 border rounded-full p-1 pl-4 focus-within:ring-1 focus-within:ring-primary/50 transition-shadow">
                        <input
                            type="text"
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Pregúntale a Wise..."
                            disabled={chatLoading}
                            className="flex-1 text-sm bg-transparent outline-none disabled:opacity-50 placeholder:text-muted-foreground/70"
                        />
                        <Button
                            size="icon"
                            onClick={sendMessage}
                            disabled={!chatInput.trim() || chatLoading}
                            className="rounded-full h-8 w-8 flex-shrink-0"
                        >
                            <Send className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )

    // ── Render Form ────────────────────────────────────────────────────────────
    const isCollidingPage = pathname.includes('/recurrentes') || pathname.includes('/checklist') || pathname.includes('/transactions')
    const fabPositionClass = isCollidingPage ? 'bottom-36 sm:bottom-6' : 'bottom-20 sm:bottom-6'

    return (
        <div className={`fixed right-4 sm:right-6 z-[60] flex flex-col items-end pointer-events-none transition-all duration-300 ${fabPositionClass}`}>
            {/* Expanded Card */}
            {isOpen && (
                <Card className="pointer-events-auto mb-4 w-[calc(100vw-2rem)] sm:w-[420px] h-[75vh] sm:h-[600px] shadow-2xl border-border flex flex-col overflow-hidden animate-in slide-in-from-bottom-6 zoom-in-95 duration-200 fade-in">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-orange-600 flex items-center justify-center shadow-sm text-white">
                                <WiseLogo />
                            </div>
                            <div>
                                <h3 className="font-semibold text-sm leading-none">Wise</h3>
                                {isPro && activeTab === 'analysis' && advice && !analysisLoading ? (
                                    <div className="mt-1"><MoodBadge mood={advice.mood} /></div>
                                ) : isPro ? (
                                    <p className="text-[10px] text-muted-foreground mt-0.5">Tu coach financiero</p>
                                ) : null}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isPro && activeTab === 'analysis' && (
                                <button
                                    onClick={() => fetchAnalysis()}
                                    disabled={analysisLoading}
                                    className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50 p-1.5"
                                    title="Actualizar análisis"
                                >
                                    <RefreshCw className={`h-4 w-4 ${analysisLoading ? 'animate-spin' : ''}`} />
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-muted-foreground hover:text-foreground transition-colors p-1.5 bg-muted/50 rounded-full hover:bg-muted"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    {isPro && (
                        <div className="flex border-b bg-muted/10">
                            <button
                                onClick={() => setActiveTab('analysis')}
                                className={`flex-1 py-2.5 text-xs font-medium transition-colors border-b-2 ${activeTab === 'analysis' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50'}`}
                            >
                                Análisis
                            </button>
                            <button
                                onClick={() => setActiveTab('chat')}
                                className={`flex-1 py-2.5 text-xs font-medium transition-colors border-b-2 ${activeTab === 'chat' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50'}`}
                            >
                                Hablar con Wise
                            </button>
                        </div>
                    )}

                    {/* Body */}
                    <CardContent className="flex-1 p-0 overflow-hidden flex flex-col relative">
                        {activeTab === 'analysis' ? analysisContent : chatContent}
                    </CardContent>
                </Card>
            )}

            {/* FAB Button */}
            {!isOpen && (
                <div className="pointer-events-auto relative">
                    <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" style={{ animationDuration: '3s' }} />
                    <button
                        onClick={() => setIsOpen(true)}
                        className="relative h-14 w-14 rounded-full shadow-lg hover:shadow-xl bg-orange-600 hover:bg-orange-700 text-white flex items-center justify-center transition-all hover:scale-110 active:scale-95 animate-in zoom-in group"
                        aria-label="Wise — Tu coach financiero"
                    >
                        <span className="text-xl font-black italic leading-none select-none group-hover:scale-110 transition-transform">W</span>
                    </button>
                </div>
            )}
        </div>
    )
}
